import assets from "assets";
import Column from "components/Column";
import ContentCardCtr from "components/ContentCard";
import { CircledImageCtr, GenericImageWrapper } from "components/Logo";
import QuestionHelper from "components/QuestionHelper";
import Row, { AutoRow } from "components/Row";
import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "theme/colors";
import { TEXT } from "theme/texts";
import { formatAmount } from "utils/numbers";
import { useCurrencyUnit } from "store/user/hooks";
import SKLTTable from "components/Skeletons/common/SKLTTable";
import { ListButtonWrapper } from "../styled";
import { isMobile } from "react-device-detect";
import { getChainTypeLogo } from "utils/logo";
import { SmallOptionButton } from "components/Button";
import { ChainType } from "types";
import { useSolanaListingAndSales } from "data/yoots/y00tsSalesAndListing";

const Entity = ({ data }: {
    data: any
}) => {
  return (
    <Row
      style={{
        gap: "2rem",
        padding: ".5rem .75rem",
        borderRadius: ".25rem",
      }}
    >
      <Row gap=".5rem">
        <CircledImageCtr radius="100%" size={"30px"}>
          <img src={data?.icon} alt="" />
        </CircledImageCtr>
        <Column
          style={{ cursor: "pointer" }}
          onClick={() => window.open(data?.link, "_blank")}
        >
          <TEXT.Body>@{data?.user}</TEXT.Body>
          <TEXT.SmallBody>
            <TEXT.Disabled>{data?.time}</TEXT.Disabled>
          </TEXT.SmallBody>
        </Column>
      </Row>
      <Column style={{ alignItems: "flex-end", gap: ".15rem" }}>
        <TEXT.Body style={{ display: "flex", gap: ".25rem" }}>
          <CircledImageCtr size="15px">
            <img src={data?.logo} alt="" style={{ objectFit: "contain" }} />
          </CircledImageCtr>

          {data?.floor}
        </TEXT.Body>
        <TEXT.SmallBody>
          <TEXT.Disabled>{data?.floor_percent}% floor</TEXT.Disabled>
        </TEXT.SmallBody>
      </Column>
    </Row>
  );
};

const ListingsTable = ({ listings }: {listings:any}) => {
  return (
    <Column style={{ width: "100%", gap: "1rem" }} className="_listingsGraph">
      <Column style={{ gap: "1rem" }} className="_listingCol">
        {listings.map((entity:any, idx:number) => (
          <Entity data={entity} key={idx} />
        ))}
      </Column>
    </Column>
  );
};
const SalesTable = ({ sales }: { sales: any}) => {
  return (
    <Column style={{ width: "100%", gap: "1rem" }} className="_salesGraph">
      <Column style={{ gap: "1rem" }} className="_salesCol">
        {sales.map((entity:any, idx:number) => (
          <Entity data={entity} key={idx} />
        ))}
      </Column>
    </Column>
  );
};

const ListingAndSales = ({ salesData, ListingData }: {
    salesData: any,
    ListingData: any
}) => {
  const [showListingData, setShowListingData] = useState(
    ListingData?.length > 0 ? true : false
  );
  const showListingsTable = () => setShowListingData(true);
  const hideListingsTable = () => setShowListingData(false);
  const [chainType, setChainType] = useState(ChainType.polygon);
  const { currency } = useCurrencyUnit();
  const solanaData = useSolanaListingAndSales();
  let solanaSalesData = solanaData?.data?.sales?.length > 0 && // sort by date
    solanaData?.data?.sales?.sort((a:any, b:any) => {
      return new Date(b?.boughtOn).getTime() - new Date(a?.boughtOn).getTime();
    });

  let solanaSales = solanaSalesData && solanaSalesData?.map((item:any, index:number) => {
    return {
      id: index + 1,
      icon: assets?.icons?.defaultCollectionIcon,
      user: item?.sellerAddress?.slice(0, 5) + "..." + item?.sellerAddress?.slice(-5),
      floor: item?.price?.toFixed(2),
      floor_percent: undefined,
      chain: "solana",
      logo: getChainTypeLogo(ChainType.sol),
      time: new Date(item?.boughtOn)?.toLocaleDateString(),
      link: `https://explorer.solana.com/tx/${item?.saleTransactionHash}`,
    };
  })?.slice(0,20)

  let solanaListingsData = solanaData?.data?.listing && solanaData?.data?.listing?.map((item:any, index:number) => {
    return {
      id: index + 1,
      icon: assets?.icons?.defaultCollectionIcon,
      user: item?.creators[0]?.address?.slice(0, 5) + "..." + item?.creators[0]?.address?.slice(-5),
      floor: item?.price?.toFixed(2),
      floor_percent: undefined,
      chain: "solana",
      logo: getChainTypeLogo(ChainType.sol),
      time: undefined,
      link: item?.buyLink,
    };
  })?.slice(0,20)



  const sales = salesData?.map((item:any, index:number) => {
    return {
      id: index + 1,
      icon: assets?.icons?.defaultCollectionIcon,
      user: item?.wallet?.slice(0, 5) + "..." + item?.wallet?.slice(-5),
      floor:
        currency?.symbol === "ETH"
          ? item?.price_in_eth?.toFixed(2)
          : currency?.symbol === "MATIC"
          ? item?.price_in_matic?.toFixed(2)
          : item?.price_in_usd?.toFixed(2),
      floor_percent:
        currency?.symbol === "ETH"
          ? item?.percentage_in_eth?.toFixed(2)
          : currency?.symbol === "MATIC"
          ? item?.percentage_in_matic?.toFixed(2)
          : item?.percentage_in_usd?.toFixed(2),
      chain: "polygon",
      logo: currency?.logo,
      time: new Date(item?.date)?.toLocaleDateString(),
      link: item?.link,
    };
  });
  const listings = ListingData?.map((item:any, index:number) => {
    return {
      id: index + 1,
      icon: assets?.icons?.defaultCollectionIcon,
      user: item?.wallet?.slice(0, 5) + "..." + item?.wallet?.slice(-5),
      floor:
        currency?.symbol === "ETH"
          ? item?.price_in_eth?.toFixed(2)
          : currency?.symbol === "MATIC"
          ? item?.price_in_matic?.toFixed(2)
          : item?.price_in_usd?.toFixed(2),
      floor_percent:
        currency?.symbol === "ETH"
          ? item?.percentage_in_eth?.toFixed(2)
          : currency?.symbol === "MATIC"
          ? item?.percentage_in_matic?.toFixed(2)
          : item?.percentage_in_usd?.toFixed(2),
      chain: "polygon",
      logo: currency?.logo,
      time: new Date(item?.date)?.toLocaleDateString(),
      link: item?.link,
    };
  });

  return (
    <ListingAndSalesCtr>
      <ContentCardCtr
        style={{
          height: "100%",
          padding: "0rem",
          gap: 0,
        }}
      >
        <Row>
        <Row
          alignItems={"center"}
          alignContent={"center"}
          justifyContent={"center"}
          className="_cardTitle"
        >
          <TEXT.MediumHeader>
            {(ListingData?.length > 0 && salesData?.length) > 0
              ? "Listing & Sales"
              : ListingData?.length > 0
              ? "Listing"
              : "Sales"}
          </TEXT.MediumHeader>
          <QuestionHelper
            text={"All the NFTs put up for sale or was sold to another wallet"}
            iconSize="24px"
          />
           </Row>
           {/* <div
              style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                padding: "0 1rem",
                 
              }}
           >
              <ListButtonWrapper withoutDivider={true}>
                  {[ChainType.polygon, ChainType.sol].map((option, index) => {
                      return (
                          <SmallOptionButton
                              key={index}
                              active={chainType === option}
                              onClick={() => setChainType(option)}
                          >
                              <Row gap="4px">
                                  <GenericImageWrapper
                                      src={getChainTypeLogo(option)}
                                      size={isMobile ? "16px" : "12px"}
                                  />
                                  {!isMobile && (
                                      <TEXT.SmallHeader fontWeight={600} fontSize={12} >
                                          {option}
                                      </TEXT.SmallHeader>
                                  )}
                              </Row>
                          </SmallOptionButton>
                      );
                  })}
              </ListButtonWrapper>

          </div> */}
         
        </Row>
        {ListingData?.length === 0 || salesData?.length === 0 ? null : (
          <div className="_graphBtns">
            <button
              onClick={showListingsTable}
              disabled={ListingData?.length === 0}
              style={showListingData ? { background: colors().primary } : {}}
            >
              Listings
            </button>

            <button
              onClick={hideListingsTable}
              disabled={salesData?.length === 0}
              style={!showListingData ? { background: colors().primary } : {}}
            >
              Sales
            </button>
          </div>
        )}
        <div className="_tableHeadings">
          {ListingData?.length === 0 || salesData?.length === 0 ? null : (
            <>
              <div className="_heading">
                <TEXT.SmallHeader>Listings</TEXT.SmallHeader>
              </div>

              <div className="_heading">
                <TEXT.SmallHeader>Sales</TEXT.SmallHeader>
              </div>
            </>
          )}
        </div>
        <Row className="_listingAndSalesGraphCtr">
          {listings?.length > 0 || solanaListingsData ? (
            <ListingsTable listings={chainType === "Polygon" ? listings : solanaListingsData ? solanaListingsData : [] } />
          ) : (
            <SKLTTable rowCount={8} />
          )}
          {sales?.length > 0 || solanaSales  ? (
            <SalesTable sales={chainType === "Polygon" ? sales : solanaSales ? solanaSales : []} />
          ) : (
            <SKLTTable rowCount={8} />
          )}
        </Row>
        <Row gap="1rem" className="_mobGraphsCtr">
          {showListingData ? (
            <ListingsTable listings={listings} />
          ) : (
            <SalesTable sales={sales} />
          )}
        </Row>
      </ContentCardCtr>
    </ListingAndSalesCtr>
  );
};
const ListingAndSalesCtr = styled(Column)`
  width: 100%;
  max-height: 550px;
  ._tableHeadings {
    display: flex;
    border-bottom: 1px solid ${(props) => props.theme.disabled};
    justify-content: space-around;
    ._heading {
      flex: 1;
      padding: 1rem;
      &:nth-of-type(1) {
        border-right: 1px solid ${(props) => props.theme.disabled};
      }
    }
  }
  ._cardTitle {
    border-bottom: 1px solid ${(props) => props.theme.disabled};
    padding: 1rem;
  }
  ._listingAndSalesGraphCtr,
  ._mobGraphsCtr {
    padding: 1rem;
    overflow-y: auto;
    align-items: flex-start;
    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
    /* Hide scrollbar for IE, Edge add Firefox */
    -ms-overflow-style: none;
    scrollbar-width: none; /* Firefox */
  }
  ._mobGraphsCtr {
    display: none;
  }
  ._listingCol,
  ._salesCol {
    height: 100%;
  }
  ._graphBtns {
    border-bottom: 1px solid ${(props) => props?.theme.disabled};
    width: 100%;
    gap: 1rem;
    display: none;
    padding: 0.5rem;
    button {
      border: none;
      outline: none;
      background: none;
      border-radius: 0.25rem;
      color: ${(props) => props?.theme.body};
      min-width: 100px;
      padding: 0.5rem 1rem;
      font-family: var(--ff-subtitle);
    }
  }
  //@media quries : mobile screens
  ${({ theme }) => theme.mediaWidth.upToSmall`
   ._tableHeadings{
    display:none;
   }
      ._listingAndSalesGraphCtr {
        display:none;
      }
      ._mobGraphsCtr{
        display:block;
      }
       ._graphBtns {
        display:block;
       }
    `}
`;

export default ListingAndSales;